import { memo, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '../../services/toastService'
import { useAuth } from '../../context/AuthContext'
import { useVendorStatus } from '../../hooks/useVendorStatus'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useImageErrorHandler } from '../../hooks/useImageErrorHandler'
import { useEntityPanelActions } from '../../hooks/useEntityPanelActions'
import { useEntityInsights } from '../../hooks/useEntityInsights'
import { checkInAPI } from '../../services/api/checkin'
import { entityAPI } from '../../services/api/entities'
import { formatRelativeTime } from '../../utils/dateFormatting'
import { createLogger } from '../../utils/logger'
import EntityActionButtons from './EntityActionButtons'
import EntityCheckInPanel from './EntityCheckInPanel'
import EntitySemiMode from './EntitySemiMode'
import EntityPanelHeader from './EntityPanelHeader'
import EntityExpandedContent from './EntityExpandedContent'
import BookingInlinePanel from '../bookings/BookingInlinePanel'
import debugWrapper from '../../utils/debugWrapper'
import { trackButtonClick, trackEvent } from '../../utils/analytics'

/** Convert ISO → local HH:MM for time input (empty if missing/expired). */
const toLocalTime = (isoStr) => {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  if (d <= new Date()) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Build ISO from HH:MM, rolling to next day if already past. */
const buildISO = (timeStr) => {
  if (!timeStr) return undefined
  const [hh, mm] = timeStr.split(':').map(Number)
  const d = new Date()
  d.setHours(hh, mm, 0, 0)
  if (d <= new Date()) d.setDate(d.getDate() + 1)
  return d.toISOString()
}

const logger = createLogger('EntityDisplayPanel')

/**
 * EntityDisplayPanel - Entity details in semi/expanded panel modes.
 * Semi: compact thumbnail row. Expanded: gallery strip, info, official menu, toggleable details.
 * @component
 * @idempotent - React.memo for optimization
 * @atomic - Single responsibility: entity panel display
 */
const EntityDisplayPanel = memo(function EntityDisplayPanel({
  // Entity data
  entityDetails,
  latestSighting,
  entityGallery = [],
  entityId,
  // User & auth
  user,
  isFavorite,
  isTogglingFavorite,
  toggleFavorite,
  entityNotifyOnSpot = false,
  onEntityNotifyToggle = null,
  // UI state
  panelState,
  onPanelStateChange,
  onTabChange,
  // Gallery state
  selectedImage,
  selectedImageIndex,
  onImageClick,
  onCloseImage,
  onPreviousImage,
  onNextImage,
  galleryRenderKey = 0,
  // Entity helpers
  getCategoryIcon,
  getCategoryColor,
  getCategoryIconColor,
  // Location
  userLocation,
  // Edit permissions
  canEditEntity,
  onNavigateToEdit,
  // Confirm location
  onConfirmLocationClick,
}) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { isVendor, vendorEntities, refetch: refetchVendorStatus } = useVendorStatus()
  const handleImageError = useImageErrorHandler()
  const { trustStats, sightingPatternCounts, peakDow, peakHour, hourlyBuckets } = useEntityInsights(
    panelState === 'expanded' ? (entityDetails?.id || entityId) : null
  )
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0)
  const [isLive, setIsLive] = useState(entityDetails?.isLive ?? false)
  const [liveUntilInput, setLiveUntilInput] = useState(() => toLocalTime(entityDetails?.liveUntil))
  const [localLiveUntil, setLocalLiveUntil] = useState(entityDetails?.liveUntil ?? null)
  const liveUntilTouched = useRef(false)
  const { execute: executeCheckIn, loading: checkInLoading } = useAsyncOperation()

  // Sync live state when entityDetails prop changes (e.g. parent re-fetches entity)
  useEffect(() => {
    setIsLive(entityDetails?.isLive ?? false)
    setLiveUntilInput(toLocalTime(entityDetails?.liveUntil))
    setLocalLiveUntil(entityDetails?.liveUntil ?? null)
    liveUntilTouched.current = false
  }, [entityDetails?.isLive, entityDetails?.liveUntil])

  const addHoursToInput = useCallback((h) => {
    const d = new Date(Date.now() + h * 3600 * 1000)
    const val = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    setLiveUntilInput(val)
    liveUntilTouched.current = true
  }, [])

  const handleCheckInToggle = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const next = !isLive
    const liveUntilISO = next ? buildISO(liveUntilInput) : undefined
    executeCheckIn(() => checkInAPI.checkIn(entityDetails?.id || entityId, next, liveUntilISO), {
      onSuccess: () => {
        setIsLive(next)
        if (next) {
          setLocalLiveUntil(liveUntilISO ?? null)
        } else {
          setLiveUntilInput('')
          setLocalLiveUntil(null)
        }
        liveUntilTouched.current = false
        toast.success(next ? t('checkin.nowOpen') : t('checkin.nowClosed'))
        logger.info('Check-in toggled', { entityId, isLive: next })
      },
      onError: (err) => {
        logger.error('Check-in failed', err)
        toast.error(err?.message || t('checkin.error'))
      },
    })
  }, [isLive, liveUntilInput, entityDetails?.id, entityId, executeCheckIn, t])

  const handleUpdateLiveUntil = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const liveUntilISO = buildISO(liveUntilInput)
    executeCheckIn(() => checkInAPI.checkIn(entityDetails?.id || entityId, true, liveUntilISO), {
      onSuccess: () => {
        setLocalLiveUntil(liveUntilISO ?? null)
        liveUntilTouched.current = false
        toast.success(t('checkin.nowOpen'))
        logger.info('Close time updated', { entityId })
      },
      onError: (err) => {
        logger.error('Close time update failed', err)
        toast.error(err?.message || t('checkin.error'))
      },
    })
  }, [liveUntilInput, entityDetails?.id, entityId, executeCheckIn, t])

  const refreshSchedule = useCallback(() => setScheduleRefreshKey(k => k + 1), [])

  const isOwnSighting = useMemo(() => {
    if (!user || !latestSighting) return false
    return (
      (latestSighting.userId && user.id === latestSighting.userId) ||
      (latestSighting.user_id && user.id === latestSighting.user_id) ||
      (latestSighting.lastConfirmerId && user.id === latestSighting.lastConfirmerId) ||
      (latestSighting.last_confirmer_id && user.id === latestSighting.last_confirmer_id)
    )
  }, [user, latestSighting])

  const {
    isJoiningChannel,
    handleFavoriteClick,
    handleJoinDiscussion,
    handleConfirmLocationClick
  } = useEntityPanelActions({
    user, entityDetails, isFavorite, toggleFavorite, isTogglingFavorite,
    onTabChange, onConfirmLocationClick, isOwnSighting, t
  })

  const handleClaimClick = useCallback(async () => {
    trackButtonClick('claim_business', 'EntityDisplayPanel', {
      entity_id: entityDetails?.id, entity_name: entityDetails?.name, is_authenticated: isAuthenticated
    })
    if (!isAuthenticated) {
      trackEvent('Conversion', 'signup_prompt', 'claim_business_clicked', null, {
        entity_id: entityDetails?.id, entity_name: entityDetails?.name, source: 'claim_business_button'
      })
      onTabChange?.('contact', null, null, { intent: 'claim_guest', entityId: entityDetails?.id, entityName: entityDetails?.name })
      return
    }
    try {
      const res = await entityAPI.claimEntity(entityDetails.id)
      if (res.data?.alreadyClaimed) {
        toast.success(t('vendor.claim.alreadyClaimed', 'You are already managing this entity'))
        onTabChange?.('vendor')
      } else {
        toast.success(t('vendor.claim.success', 'You are now managing this entity'))
        await refetchVendorStatus()
        onTabChange?.('vendor')
      }
    } catch (err) {
      const errCode = err?.response?.data?.error
      if (errCode === 'proof_of_presence_required') {
        toast.error(t('vendor.claim.popRequired', 'Visit and spot this entity first to claim stewardship'))
      } else {
        toast.error(err?.response?.data?.message || t('errors.generic', 'Something went wrong'))
      }
    }
  }, [isAuthenticated, entityDetails?.id, entityDetails?.name, onTabChange, refetchVendorStatus, t])

  const handleReportClick = useCallback(() => {
    trackButtonClick('report_listing', 'EntityDisplayPanel', {
      entity_id: entityDetails?.id, is_authenticated: isAuthenticated
    })
    if (!isAuthenticated) {
      onTabChange?.('contact', null, null, { intent: 'report_guest', entityId: entityDetails?.id, entityName: entityDetails?.name })
    } else {
      onTabChange?.('contact', null, null, { intent: 'report', entityId: entityDetails?.id, entityName: entityDetails?.name })
    }
  }, [isAuthenticated, entityDetails?.id, entityDetails?.name, onTabChange])

  const primaryImage = entityGallery?.find(img => img.isPrimary)
  const firstImage = entityGallery?.[0]
  const imageUrl = primaryImage?.imageUrl || firstImage?.imageUrl || entityDetails.image_url || entityDetails.imageUrl
  const isAlreadySteward = isVendor && vendorEntities?.some(e => e.id === entityDetails?.id)
  const IconComponent = getCategoryIcon ? getCategoryIcon(entityDetails.category) : null
  const bgColor = getCategoryColor ? getCategoryColor(entityDetails.category) : 'from-primary/5 to-primary/10'
  const iconColor = getCategoryIconColor ? getCategoryIconColor(entityDetails.category) : 'text-primary'
  const lastSeen = entityDetails.lastSeenAt || entityDetails.last_seen_at
  const lastSeenText = lastSeen ? formatRelativeTime(lastSeen) : null
  const [showBookingPanel, setShowBookingPanel] = useState(false)
  const handleBook = useCallback((e) => {
    e?.stopPropagation?.()
    if (!isAuthenticated) { onTabChange?.('auth'); return }
    setShowBookingPanel(true)
  }, [isAuthenticated, onTabChange])

  // Listen for booking triggered from map popups (entity-mode variant)
  useEffect(() => {
    const targetId = entityDetails?.id || entityId
    logger.info('Registering trckit:book listener', { targetId })
    const handler = (e) => {
      logger.info('trckit:book event received', { receivedId: e.detail?.entityId, targetId, match: e.detail?.entityId === targetId })
      if (e.detail?.entityId === targetId) {
        if (isAuthenticated) setShowBookingPanel(true)
        else onTabChange?.('auth')
      }
    }
    window.addEventListener('trckit:book', handler)
    return () => window.removeEventListener('trckit:book', handler)
  }, [entityDetails?.id, entityId, isAuthenticated, onTabChange])

  const hasBookableOfferings = !!(entityDetails?.hasBookableOfferings ?? entityDetails?.has_bookable_offerings)

  if (showBookingPanel) {
    return (
      <BookingInlinePanel
        entityId={entityDetails.id}
        entityName={entityDetails.name}
        onClose={() => setShowBookingPanel(false)}
        entityContact={{
          phone: entityDetails.phone,
          email: entityDetails.email,
          website: entityDetails.website,
          tipUrl: entityDetails.tipUrl ?? entityDetails.tip_url,
        }}
      />
    )
  }

  return (
    <>
      {/* Confirm + action buttons (shown in both semi and expanded) */}
      {(panelState === 'expanded' || panelState === 'semi') && (
        <>
          <EntityActionButtons
            handleConfirmLocationClick={handleConfirmLocationClick}
            isOwnSighting={isOwnSighting}
            handleFavoriteClick={handleFavoriteClick}
            isFavorite={isFavorite}
            isTogglingFavorite={isTogglingFavorite}
            handleJoinDiscussion={handleJoinDiscussion}
            isJoiningChannel={isJoiningChannel}
            entityDetails={entityDetails}
            notifyOnSpot={entityNotifyOnSpot}
            onToggleNotify={onEntityNotifyToggle}
            onBook={hasBookableOfferings ? handleBook : null}
            t={t}
          />
          {canEditEntity && (
            <EntityCheckInPanel
              isLive={isLive}
              checkInLoading={checkInLoading}
              handleCheckInToggle={handleCheckInToggle}
              liveUntilInput={liveUntilInput}
              onLiveUntilChange={(val) => { setLiveUntilInput(val); liveUntilTouched.current = true }}
              addHoursToInput={addHoursToInput}
              handleUpdateLiveUntil={handleUpdateLiveUntil}
              showUpdateButton={isLive && liveUntilTouched.current}
              t={t}
            />
          )}
        </>
      )}

      {/* Semi mode: compact thumbnail row */}
      {panelState === 'semi' && (
        <EntitySemiMode
          entityDetails={entityDetails}
          imageUrl={imageUrl}
          IconComponent={IconComponent}
          bgColor={bgColor}
          iconColor={iconColor}
          isLive={isLive}
          localLiveUntil={localLiveUntil}
          handleImageError={handleImageError}
          onExpand={onPanelStateChange}
          t={t}
        />
      )}

      {/* Expanded mode */}
      {panelState === 'expanded' && (
        <>
          {/* Header: name + category chip + last-seen + collapse button */}
          <EntityPanelHeader
            entityDetails={entityDetails}
            isLive={isLive}
            localLiveUntil={localLiveUntil}
            lastSeenText={lastSeenText}
            onCollapse={onPanelStateChange}
          />

          {/* Gallery, sections, CTAs */}
          <EntityExpandedContent
            entityGallery={entityGallery}
            entityId={entityId}
            galleryRenderKey={galleryRenderKey}
            selectedImage={selectedImage}
            selectedImageIndex={selectedImageIndex}
            onImageClick={onImageClick}
            onCloseImage={onCloseImage}
            onPreviousImage={onPreviousImage}
            onNextImage={onNextImage}
            imageUrl={imageUrl}
            primaryImage={primaryImage}
            IconComponent={IconComponent}
            bgColor={bgColor}
            iconColor={iconColor}
            handleImageError={handleImageError}
            entityDetails={entityDetails}
            userLocation={userLocation}
            scheduleRefreshKey={scheduleRefreshKey}
            handleClaimClick={handleClaimClick}
            isAlreadySteward={isAlreadySteward}
            onReportClick={handleReportClick}
            trustStats={trustStats}
            sightingPatternCounts={sightingPatternCounts}
            peakDow={peakDow}
            peakHour={peakHour}
            hourlyBuckets={hourlyBuckets}
            t={t}
          />
        </>
      )}

      {/* Schedule manager modal (vendors only) */}


    </>
  )
})

export default debugWrapper(EntityDisplayPanel, 'EntityDisplayPanel', 'src/components/map/EntityDisplayPanel.jsx')
